import { useState, useEffect, useCallback } from "react"
import { getDatabase, ref, set, get, onValue, DataSnapshot, update } from "firebase/database"
import { getAuth, signInAnonymously } from "firebase/auth"

import { Account8 } from "seenspire-api-client"
import { useUserAgent } from "../../hooks/useUserAgent"

export interface MonitorsData {
  [key: string]: Monitor | { [key: string]: Monitor },
}

export interface Monitor {
  uid: string
  name: string
  accountSlug: string
  feedUrl?: string
  feedName?: string
  status?: string
  createdAt?: string
  deviceType?: string
}


interface UseFirebaseAnonReturnType {
  data: Monitor[] | null
  loading: boolean
  initialLoadComplete: boolean
  error: string | null
  uid: string | null
  getMonitor: (device: string) => Promise<Monitor | null>
  removeMonitor: (device: string) => Promise<void>
  getDevices: () => Promise<void>
  setMonitor: (deviceCode: string, deviceName: string) => Promise<Monitor | void>
  setFeed: (url: string, streamName: string, deviceId: string) => Promise<void>
}

const useFirebaseAnon = (account: Account8): UseFirebaseAnonReturnType => {

  const [data, setData] = useState<Monitor[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [uid, setUid] = useState<string | null>(null)

  const {parse} = useUserAgent();

  const getDevices = useCallback(async () => {

    if (!account) return;

    const auth = getAuth()

    const authenticateAnonymously = async () => {

      try {
        const user = await signInAnonymously(auth)

        const realtimeUuid = account?.realtime_id;

        if (user && user.user && user.user.uid && realtimeUuid) {

          setUid(realtimeUuid)
        }
      } catch (error: any) {

        setError(error ? error.message : error)
        setLoading(false)
      }
    };

    if (!auth.currentUser) {

      await authenticateAnonymously()
    }

    const realtimeUuid = account?.realtime_id;
    setUid(realtimeUuid ?? null)

  }, [account])

  useEffect(() => {

    getDevices();
  }, [getDevices])

  useEffect(() => {
    if (!account) return;

    if (!uid || uid.trim() === "") {
      setError('Invalid or empty UID provided. - 1')
      return undefined
    }

    const db = getDatabase();
    const mainRef = ref(db, `mobile/accounts_new/${uid}`);

    const secondaryListeners: { [key: string]: () => void } = {};
    let pendingSecondaryLoads = 0;

    const handleValueChange = (snapshot: DataSnapshot | null) => {
      const mainData = (snapshot ? snapshot.val() : {}) as MonitorsData;
      const mixedData = { ...mainData };

      const updateSecondaryData = (key: string, secondaryData: any) => {
        if (key === 'zoom') {
          const zoomData = mixedData.zoom || {};
          Object.entries(zoomData).forEach(([zoomId, zoomMonitor]) => {
            if (!mixedData.zoom) mixedData.zoom = {};
            (mixedData.zoom as { [key: string]: Monitor })[zoomId] = {
              ...zoomMonitor,
              deviceType: "Zoom Device",
              status: "active"
            };
          });
        } else {
          const ua = parse(secondaryData?.ua);
          const _dt = ua.browser && ua.device ? `${ua.os}, ${ua.browser}` : "-"
          mixedData[key] = {
            ...(mixedData[key] as Monitor),
            status: secondaryData.status,
            deviceType: _dt,
          } as Monitor;
        }
        pendingSecondaryLoads--;

        const regularMonitors = Object.entries(mixedData)
          .filter(([k, v]) => k !== 'zoom' && v)
          .map(([_, v]) => v);

        const zoomMonitors = mixedData?.zoom ? 
          Object.entries(mixedData.zoom)
            .map(([_, v]) => v) : 
          [];

        const allMonitors = [...regularMonitors, ...zoomMonitors]
          .sort((a, b) => b.name > a.name ? -1 : 1);

        setData(allMonitors);
        
        if (pendingSecondaryLoads === 0) {
          setInitialLoadComplete(true);
          setLoading(false);
        }
      };

      Object.values(secondaryListeners).forEach(unsubscribe => unsubscribe());

      pendingSecondaryLoads = 0;

      if (mainData) {
        Object.entries(mainData).forEach(([key, value]) => {
          if (key !== 'zoom' && value?.uid) {
            pendingSecondaryLoads++;
            const secondaryRef = ref(db, `mobile/connect/${value.uid}`);
            const unsubscribe = onValue(secondaryRef, (secondarySnapshot) => {
              const secondaryData = secondarySnapshot.val();
              updateSecondaryData(key, secondaryData);
            });
            secondaryListeners[key] = unsubscribe;
          }
        });

        if (mainData.zoom) {
          pendingSecondaryLoads++;
          
          // Process zoom monitors
          Object.entries(mainData.zoom).forEach(([zoomId, zoomMonitor]) => {
            if (zoomMonitor?.uid) {
              const zoomSecondaryRef = ref(db, `mobile/connect/zoom/${zoomId}`);
              const unsubscribe = onValue(zoomSecondaryRef, (secondarySnapshot) => {
                const secondaryData = secondarySnapshot.val();
                // We still use 'zoom' as the key here to ensure it's processed correctly in updateSecondaryData
                updateSecondaryData('zoom', secondaryData);
              });
              secondaryListeners[`zoom_${zoomId}`] = unsubscribe;
            }
          });
          
          if (Object.keys(mainData.zoom).length === 0) {
            updateSecondaryData('zoom', {});
          }
        }
      } else {
        setData(null);
      }

      if (pendingSecondaryLoads === 0) {
        setInitialLoadComplete(true);
        setLoading(false);
      }
    };

    const unsubscribeMain = onValue(mainRef, handleValueChange);

    return () => {
      unsubscribeMain();
      Object.values(secondaryListeners).forEach(unsubscribe => unsubscribe());
    };
  }, [uid, account]);

  const setMonitor = useCallback(async (deviceCode: string, deviceName: string): Promise<Monitor | void> => {

    const db = getDatabase()
    try {
      // Check regular code
      const deviceCodeRef = ref(db, `mobile/codes/${deviceCode}`);
      const snapshot = await get(deviceCodeRef);
      
      // Check zoom code
      const zoomCodeRef = ref(db, `mobile/codes/zoom/${deviceCode}`);
      const zoomSnapshot = await get(zoomCodeRef);
      
      if (snapshot.exists() || zoomSnapshot.exists()) {
        const deviceUid = snapshot.exists() ? snapshot.val() : zoomSnapshot.val();  
        
        try {
          const poolRef = ref(db, `mobile/pool/${deviceUid}`)
          await set(poolRef, { uid: zoomSnapshot.exists() ? `zoom/${deviceUid}` : deviceUid, name: deviceName, accountSlug: account.organization_info.slug, createdAt: new Date().toISOString() })
          const accRef = ref(db, `mobile/accounts_new/${uid}/${deviceUid}`)
          await set(accRef, { uid: zoomSnapshot.exists() ? `zoom/${deviceUid}` : deviceUid, name: deviceName, accountSlug: account.organization_info.slug, createdAt: new Date().toISOString() })

          setData(prev => [...(prev ?? []), { uid: deviceUid, name: deviceName, accountSlug: account.organization_info.slug, createdAt: new Date().toISOString() }])

          return { uid: zoomSnapshot.exists() ? `zoom/${deviceUid}` : deviceUid, name: deviceName, accountSlug: account.organization_info.slug, createdAt: new Date().toISOString() }
        } catch (error) {
          if (error instanceof Error && error.message.includes('Permission denied')) {
            throw new Error('You do not have permission to add this screen. Please contact your administrator.');
          }
          throw error;
        }
      } else {
        throw new Error('Invalid screen code. Please check and try again.');
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('Permission denied')) {
        throw new Error('You do not have permission to read screen codes. Please contact your administrator.');
      }
      throw error;
    }
  }, [uid, account])

  const getMonitor = useCallback(async (device: string): Promise<Monitor | null> => {
    
    if (!account) return null;

    const isCode = device.length === 6
    
    const db = getDatabase()
    
    if (isCode) {
      // First check regular code
      const codeRef = ref(db, `mobile/codes/${device}`)
      const codeSnapshot = await get(codeRef)
      
      if (codeSnapshot.exists()) {
        const deviceId = codeSnapshot.val()
        const deviceRef = ref(db, `mobile/pool/${deviceId}`)
        const deviceSnapshot = await get(deviceRef)
        return deviceSnapshot.exists() ? deviceSnapshot.val() : null
      }
      
      // Then check zoom code
      const zoomCodeRef = ref(db, `mobile/codes/zoom/${device}`)
      const zoomCodeSnapshot = await get(zoomCodeRef)
      
      if (zoomCodeSnapshot.exists()) {
        const deviceId = zoomCodeSnapshot.val()
        const deviceRef = ref(db, `mobile/pool/${deviceId}`)
        const deviceSnapshot = await get(deviceRef)
        return deviceSnapshot.exists() ? deviceSnapshot.val() : null
      }
    } else {
      // Direct device ID lookup
      const deviceRef = ref(db, `mobile/pool/${device}`)
      const deviceSnapshot = await get(deviceRef)
      if (deviceSnapshot.exists()) {
        return deviceSnapshot.val()
      }
    }

    throw new Error('Monitor is not found.')
  }, [account])

  const removeMonitor = useCallback(async (device: string): Promise<void> => {
    if (!account) return;

    const isCode = device.length === 6;

    if (uid) {
      const db = getDatabase();

      let deviceId = device;
      if (isCode) {
        const deviceCodeRef = ref(db, `mobile/codes/${device}`);
        const deviceCodeRefZoom = ref(db, `mobile/codes/zoom/${device}`);
        const snapshot = await get(deviceCodeRef);
        const snapshotZoom = await get(deviceCodeRefZoom);
        if (snapshot.exists()) {
          deviceId = snapshot.val();  
          await set(deviceCodeRef, null);
        }
        if (snapshotZoom.exists()) {
          deviceId = snapshotZoom.val();
          await set(deviceCodeRefZoom, null);
        }
      }

      // Check where the device exists
      const accRef = ref(db, `mobile/accounts_new/${uid}/${deviceId}`);
      const zoomRef = ref(db, `mobile/accounts_new/${uid}/zoom/${deviceId}`);
      
      const [accSnapshot, zoomSnapshot] = await Promise.all([
        get(accRef),
        get(zoomRef)
      ]);

      if (accSnapshot.exists()) {
        // Regular device
        const poolRef = ref(db, `mobile/pool/${deviceId}`);
        await set(poolRef, null);
        await set(accRef, null);
      } else if (zoomSnapshot.exists()) {
        // Zoom device
        const poolRef = ref(db, `mobile/pool/${deviceId}`);
        await set(poolRef, null);
        await set(zoomRef, null);
      }
    } else {
      throw new Error('Invalid or empty UID provided. - 3');
    }
  }, [uid, account, data]);

  const setFeed = useCallback(async (url: string, streamName: string, deviceId: string): Promise<void> => {
    if (uid) {
      const db = getDatabase();
      
      // Check where the device exists
      const accRef = ref(db, `mobile/accounts_new/${uid}/${deviceId}`);
      const zoomRef = ref(db, `mobile/accounts_new/${uid}/zoom/${deviceId}`);
      
      const [accSnapshot, zoomSnapshot] = await Promise.all([
        get(accRef),
        get(zoomRef)
      ]);

      if (accSnapshot.exists()) {
        // Regular device
        const poolRef = ref(db, `mobile/pool/${deviceId}`);
        await update(poolRef, { feedUrl: url, feedName: streamName });
        await update(accRef, { feedUrl: url, feedName: streamName });
      } else if (zoomSnapshot.exists()) {
        // Zoom device
        const poolRef = ref(db, `mobile/pool/${deviceId}`);
        await update(poolRef, { feedUrl: url, feedName: streamName });
        await update(zoomRef, { feedUrl: url, feedName: streamName });
      }
    } else {
      throw new Error('Invalid or empty UID provided. - 4');
    }
  }, [uid]);

  return { uid, data, loading, error, getMonitor, removeMonitor, getDevices, initialLoadComplete, setMonitor, setFeed }
}

export default useFirebaseAnon